import KogModalFadeComponent from '@/main/components/modal/ModalFadeComponent';

import { quoteSupplierItemApi } from '@/main/api';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KOGFileUploader from '@/main/components/shared/fileUploader/fileUploader';
import KogModalSlideImages from '@/main/components/shared/KogModalSlideImages/modalSlideImages';
import {
  QuoteSupplierItemImageResponse,
  QuoteSupplierItemPartResponse
} from '@/main/models/response';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';
import { isNullUndefined } from '@/main/services/reducerUtils';
import { getTranslateByKey } from '@/main/services/utils';
import { convertImageToBase64 } from '@/main/services/utilsFile';
import { useParameterByTypeScreenReducer } from '@/main/store/parameterByTypeScreen';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as S from './styles';

interface CustomFile extends File {
  guid?: string;
  previewUrl?: string;
}

interface IModalUploadImage {
  quoteSupplierItemPart: QuoteSupplierItemPartResponse | null;
  isOpen: boolean;
  handleClose: () => void;
  updateItem: (
    guidQuoteSupplierItem: string,
    images: QuoteSupplierItemImageResponse[],
    itemIdUnique: string
  ) => void;
  loadedImages?: QuoteSupplierItemImageResponse[];
  allowEdit: boolean;
}

const MAXIMUM_FILES = 2;

function ModalUploadImageContent({
  loadedImages = [],
  handleClose,
  quoteSupplierItemPart,
  updateItem,
  isOpen,
  allowEdit
}: Readonly<IModalUploadImage>) {
  const [images, setImages] = useState<CustomFile[]>([]);
  const [guidsToDelete, setGuidsToDelete] = useState<string[]>([]);
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);

  const [isOpenModalFade, setIsOpenModalFade] = useState<boolean>(false);
  const [imageIndex, setImageIndex] = useState<number>(0);

  const currentPartId = quoteSupplierItemPart?.idUnique;

  useEffect(() => {
    if (!isOpen || !quoteSupplierItemPart) {
      setImages([]);
      setGuidsToDelete([]);
      return;
    }

    if (!loadedImages || loadedImages.length === 0) {
      setImages([]);
      setGuidsToDelete([]);
      return;
    }

    const files = loadedImages.map(img => {
      const byteArray = Uint8Array.from(atob(img.base64File), c =>
        c.charCodeAt(0)
      );

      const file = new File([byteArray], img.guidQuoteSupplierItemImage, {
        type: 'image/jpeg'
      }) as CustomFile;

      file.guid = img.guidQuoteSupplierItemImage;
      file.previewUrl = URL.createObjectURL(file);

      return file;
    });

    setImages(files);
    setGuidsToDelete([]);

    return () => {
      files.forEach(file => {
        if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
      });
    };
  }, [isOpen, currentPartId]);

  const maxFileSize = useParameterByTypeScreenReducer(
    ParameterTypeEnum.MaximumSupplierItemImageSize
  );

  const hasChanges = images.some(img => !img.guid) || guidsToDelete.length > 0;

  const disabledUpload =
    !hasChanges ||
    !allowEdit ||
    images.length > MAXIMUM_FILES ||
    (images.length === 0 && guidsToDelete.length === 0);

  function handleCustomDelete(nameFile: string) {
    const fileToDelete = images.find(img => img.name === nameFile);

    if (fileToDelete?.guid) {
      const guidToDelete = fileToDelete.guid;

      setGuidsToDelete(prev => [...prev, guidToDelete]);
    }

    if (fileToDelete?.previewUrl) {
      URL.revokeObjectURL(fileToDelete.previewUrl);
    }

    setImages(prev => prev.filter(img => img.name !== nameFile));
  }

  function handleImagePreviewClick(index: number) {
    setImageIndex(index);
    setIsOpenModalFade(true);
  }

  async function handleSave() {
    if (!allowEdit) return;
    if (isNullUndefined(quoteSupplierItemPart)) return;
    try {
      setIsLoadingSave(true);
      const newImages = images.filter(img => !img.guid);

      let finalImagesList: QuoteSupplierItemImageResponse[] =
        loadedImages.filter(
          img => !guidsToDelete.includes(img.guidQuoteSupplierItemImage)
        );

      let currentGuidQuoteSupplierItem =
        quoteSupplierItemPart.guidQuoteSupplierItem;

      if (guidsToDelete.length > 0) {
        await quoteSupplierItemApi.deleteImage(guidsToDelete);
      }

      if (newImages.length > 0) {
        const res = await quoteSupplierItemApi.uploadImage({
          images: newImages,
          guidQuoteItem: quoteSupplierItemPart.guidQuoteItem,
          guidQuoteSupplierItem: quoteSupplierItemPart.guidQuoteSupplierItem,
          idPartType: quoteSupplierItemPart.idPartType
        });

        const { result } = res.data;

        const uploadedResult: QuoteSupplierItemImageResponse[] =
          result.images || [];

        currentGuidQuoteSupplierItem = result.guidQuoteSupplierItem;

        const formattedNewImages: QuoteSupplierItemImageResponse[] =
          await Promise.all(
            newImages.map(async (file, index) => {
              const base64Local = await convertImageToBase64(file);
              const serverGuid =
                uploadedResult[index]?.guidQuoteSupplierItemImage || '';

              return {
                guidQuoteSupplierItemImage: serverGuid,
                base64File: base64Local.base64Image
              } as QuoteSupplierItemImageResponse;
            })
          );

        finalImagesList = [...finalImagesList, ...formattedNewImages];
      }

      const itemIdUnique = quoteSupplierItemPart?.idUnique ?? '';

      updateItem(currentGuidQuoteSupplierItem, finalImagesList, itemIdUnique);

      toast.success(getTranslateByKey('information.imageUploadSuccess'));
      handleClose();
    } catch (error) {
    } finally {
      setIsLoadingSave(false);
    }
  }

  const slideImagesArray = useMemo(() => {
    return images
      .map(img => {
        if (img.guid && img.name === img.guid) {
          const originalLoaded = loadedImages.find(
            l => l.guidQuoteSupplierItemImage === img.guid
          );
          if (originalLoaded?.base64File) {
            return `data:image/jpeg;base64,${originalLoaded.base64File}`;
          }
        }

        if (!img.previewUrl) {
          img.previewUrl = URL.createObjectURL(img);
        }

        return img.previewUrl;
      })
      .filter((url): url is string => !!url);
  }, [images, loadedImages]);

  return (
    <S.BoxModal>
      <S.Title>
        {getTranslateByKey('information.titleUploadImagesSupplier')}
      </S.Title>
      <S.FileUploaderWrapepr>
        <KOGFileUploader
          files={images as File[]}
          setFiles={setImages as any}
          canUpdateFiles={allowEdit}
          displayPreviewImages="row"
          showPreviewImages
          maxFiles={MAXIMUM_FILES}
          maxFileSize={maxFileSize}
          maxTotalSize={MAXIMUM_FILES * maxFileSize}
          typeFiles={['image']}
          isFullScreenDragDrop
          isRequired
          onCustomDelete={handleCustomDelete}
          handleClickPreviewCustom={handleImagePreviewClick}
        />
      </S.FileUploaderWrapepr>
      <S.AreaButtons>
        <SecondaryButton
          id="id-uploadImage-btn-cancel"
          onClick={() => handleClose()}>
          {getTranslateByKey('information.cancel')}
        </SecondaryButton>
        {allowEdit && (
          <Button
            id="id-uploadImage-btn-upload"
            disabled={disabledUpload}
            isLoading={isLoadingSave}
            onClick={() => handleSave()}>
            {getTranslateByKey('information.upload')}
          </Button>
        )}
      </S.AreaButtons>

      {slideImagesArray.length > 0 && (
        <KogModalSlideImages
          images={slideImagesArray}
          imageIndex={imageIndex}
          isOpen={isOpenModalFade}
          setIsOpen={setIsOpenModalFade}
          isInModal
          totalImages={slideImagesArray.length}
        />
      )}
    </S.BoxModal>
  );
}

export default function ModalUploadImage(props: Readonly<IModalUploadImage>) {
  if (isNullUndefined(props.quoteSupplierItemPart)) return;
  return (
    <KogModalFadeComponent setOpen={props.handleClose} open={props.isOpen}>
      <ModalUploadImageContent {...props} />
    </KogModalFadeComponent>
  );
}
